import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface InTransitItem {
  transfer_id: string;
  transfer_number: string;
  transfer_status: string;
  from_location_id: string;
  from_location_name: string;
  to_location_id: string;
  to_location_name: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  quantity_sent: number;
  quantity_received: number;
  in_transit: number;
  shipped_at: string | null;
  expected_date: string | null;
}

export interface LostInTransitItem {
  transfer_id: string;
  transfer_number: string;
  from_location_name: string;
  to_location_name: string;
  product_id: string;
  product_name: string;
  quantity_sent: number;
  quantity_received: number;
  quantity_lost: number;
  received_at: string | null;
}

export interface InTransitSummary {
  total_transfers: number;
  total_items: number;
  total_units_in_transit: number;
}

export interface InTransitFilters {
  fromLocationId?: string;
  toLocationId?: string;
}

export function useInTransitReport(filters: InTransitFilters = {}) {
  const { organization } = useAuth();
  const organizationId = organization?.id;
  const queryClient = useQueryClient();

  // Fetch in-transit items
  const {
    data: inTransitItems,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['in-transit-items', organizationId, filters],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc('get_in_transit_items', {
        p_organization_id: organizationId,
        p_from_location_id: filters.fromLocationId || null,
        p_to_location_id: filters.toLocationId || null,
      });

      if (error) throw error;
      return (data || []) as InTransitItem[];
    },
    enabled: !!organizationId,
  });

  // Fetch summary
  const {
    data: summary,
    isLoading: summaryLoading,
  } = useQuery({
    queryKey: ['in-transit-summary', organizationId, filters.toLocationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase.rpc('get_in_transit_summary', {
        p_organization_id: organizationId,
        p_location_id: filters.toLocationId || null,
      });

      if (error) throw error;
      return (data?.[0] || null) as InTransitSummary | null;
    },
    enabled: !!organizationId,
  });

  // Fetch lost in transit items
  const {
    data: lostItems,
    isLoading: lostLoading,
  } = useQuery({
    queryKey: ['lost-in-transit', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc('get_lost_in_transit', {
        p_organization_id: organizationId,
      });

      if (error) throw error;
      return (data || []) as LostInTransitItem[];
    },
    enabled: !!organizationId,
  });

  // Group by transfer
  const groupedByTransfer = (inTransitItems || []).reduce((acc, item) => {
    if (!acc[item.transfer_id]) {
      acc[item.transfer_id] = {
        transferId: item.transfer_id,
        transferNumber: item.transfer_number,
        status: item.transfer_status,
        fromLocationId: item.from_location_id,
        fromLocationName: item.from_location_name,
        toLocationId: item.to_location_id,
        toLocationName: item.to_location_name,
        shippedAt: item.shipped_at,
        expectedDate: item.expected_date,
        items: [],
        totalInTransit: 0,
      };
    }
    acc[item.transfer_id].items.push(item);
    acc[item.transfer_id].totalInTransit += item.in_transit;
    return acc;
  }, {} as Record<string, {
    transferId: string;
    transferNumber: string;
    status: string;
    fromLocationId: string;
    fromLocationName: string;
    toLocationId: string;
    toLocationName: string;
    shippedAt: string | null;
    expectedDate: string | null;
    items: InTransitItem[];
    totalInTransit: number;
  }>);

  // Group by product
  const groupedByProduct = (inTransitItems || []).reduce((acc, item) => {
    if (!acc[item.product_id]) {
      acc[item.product_id] = {
        productId: item.product_id,
        productName: item.product_name,
        productSku: item.product_sku,
        transfers: [],
        totalInTransit: 0,
      };
    }
    acc[item.product_id].transfers.push(item);
    acc[item.product_id].totalInTransit += item.in_transit;
    return acc;
  }, {} as Record<string, {
    productId: string;
    productName: string;
    productSku: string | null;
    transfers: InTransitItem[];
    totalInTransit: number;
  }>);

  // Return item mutation (mark as returned to sender)
  const returnItem = useMutation({
    mutationFn: async ({ 
      transferId, 
      productId, 
      quantity: _quantity 
    }: { 
      transferId: string; 
      productId: string; 
      quantity: number;
    }) => {
      // Update the transfer item to reduce in-transit quantity
      // This would typically involve creating a return record or updating the transfer
      const { error } = await supabase
        .from('goods_transfer_items')
        .update({
          quantity_received: supabase.rpc('coalesce', { 
            val: 'quantity_received',
            default_val: 0 
          }),
          variance_reason: 'Returned to sender',
        })
        .eq('transfer_id', transferId)
        .eq('product_id', productId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['in-transit-items'] });
      queryClient.invalidateQueries({ queryKey: ['in-transit-summary'] });
    },
  });

  // Export to CSV
  const exportToCSV = () => {
    if (!inTransitItems?.length) return '';

    const headers = [
      'Transfer #',
      'Status',
      'From Location',
      'To Location',
      'Product',
      'SKU',
      'Sent',
      'Received',
      'In Transit',
      'Shipped',
      'Expected',
    ];

    const rows = inTransitItems.map((item) => [
      item.transfer_number,
      item.transfer_status,
      item.from_location_name,
      item.to_location_name,
      item.product_name,
      item.product_sku || '',
      item.quantity_sent,
      item.quantity_received,
      item.in_transit,
      item.shipped_at ? new Date(item.shipped_at).toLocaleDateString() : '',
      item.expected_date ? new Date(item.expected_date).toLocaleDateString() : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = () => {
    const csvContent = exportToCSV();
    if (!csvContent) return;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `in-transit-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    inTransitItems: inTransitItems || [],
    lostItems: lostItems || [],
    summary,
    groupedByTransfer: Object.values(groupedByTransfer),
    groupedByProduct: Object.values(groupedByProduct),
    isLoading: isLoading || summaryLoading || lostLoading,
    error,
    refetch,
    returnItem,
    exportToCSV,
    downloadCSV,
  };
}
